import {appConfig} from "@/app-config";
import {OmitKeyof, onlineManager} from "@tanstack/react-query";
import {PersistQueryClientOptions} from "@tanstack/query-persist-client-core";

// Web-compatible localStorage persister
const createLocalStoragePersister = (key: string) => {
    return {
        persistClient: async (client: any) => {
            try {
                if (typeof window !== 'undefined' && window.localStorage) {
                    localStorage.setItem(key, JSON.stringify(client));
                }
            } catch (error) {
                console.warn('Failed to persist query client to localStorage:', error);
            }
        },
        restoreClient: async (): Promise<any> => {
            try {
                if (typeof window !== 'undefined' && window.localStorage) {
                    const stored = localStorage.getItem(key);
                    return stored ? JSON.parse(stored) : undefined;
                }
            } catch (error) {
                console.warn('Failed to restore query client from localStorage:', error);
            }
            return undefined;
        },
        removeClient: async () => {
            try {
                if (typeof window !== 'undefined' && window.localStorage) {
                    localStorage.removeItem(key);
                }
            } catch (error) {
                console.warn('Failed to remove query client from localStorage:', error);
            }
        }
    };
};

export const asyncStoragePersister = createLocalStoragePersister('TANSTACK_QUERY_OFFLINE_CACHE');

export const persistOptions: OmitKeyof<PersistQueryClientOptions, 'queryClient'> = {
    persister: asyncStoragePersister,
    maxAge: appConfig.appDataPersistence.maxAge,
    buster: appConfig.appDataPersistence.dataVersion.toString(),
    dehydrateOptions: {
        shouldDehydrateQuery: () => true // always dehydrate bc we also want error states that still have the stale data...
    }
};

export const registerOnlineManager = () => {
    // Only register online manager in browser environment
    if (typeof window !== 'undefined' && 'navigator' in window) {
        onlineManager.setEventListener((setOnline) => {
            // Set initial state
            setOnline(navigator.onLine);
            
            // Listen for online/offline events
            const handleOnline = () => setOnline(true);
            const handleOffline = () => setOnline(false);
            
            window.addEventListener('online', handleOnline);
            window.addEventListener('offline', handleOffline);
            
            // Return cleanup function
            return () => {
                window.removeEventListener('online', handleOnline);
                window.removeEventListener('offline', handleOffline);
            };
        });
    } else {
        // For server-side rendering or static export, assume online
        onlineManager.setOnline(true);
    }
};